<script setup lang="ts">
import type { ReportFilters } from '~/models/ReportFilters'
import type { ContentDetailsTestItem, UserTaskData } from '~/models/Report'
import type { GroupMember } from '~/models/Group'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck } from '@fortawesome/duotone-regular-svg-icons'
import { extractTaskResult, getTaskCompletionStatus, resolveUserName } from '~/utils/reportUtils'
import { getTaskItemIcon } from '~/utils/reportIcons'
import { PendoFeature } from '~/models/Pendo'
import { useTime } from '~/composables/useTime'
import useGroupsStore from '~/stores/groups'

const props = defineProps<{
  userTask: UserTaskData
  filters?: ReportFilters
  totalTaskCount?: number
}>()

const emit = defineEmits<{ click: [] }>()

const { t } = useI18n()
const { formatReportTimestamp } = useTime()

const result = computed(() => extractTaskResult(props.userTask))
const completionStatus = computed(() => getTaskCompletionStatus(props.userTask))
const items = computed(() => result.value.items)
const renderItems = computed(() => items.value.slice().sort((a, b) => a.order - b.order))

const displayName = computed(() => {
  const uid = props.userTask?.userId ?? props.userTask?.user?.userId ?? null
  if (!uid) {
    return '—'
  }

  let groupMember: GroupMember | undefined
  if (props.filters?.selectedGroup?.groupId) {
    const groupsStore = useGroupsStore()
    groupMember = groupsStore.findGroupMembersById(props.filters.selectedGroup.groupId)
      .find(member => member.userId === uid)
  }

  return resolveUserName(groupMember, props.userTask?.user)
})

function getTaskCircleIcon(item: ContentDetailsTestItem | null) {
  return getTaskItemIcon(item, t)
}

const completedText = computed(() => t('reports.tasks.completed'))
const startedText = computed(() => t('reports.tasks.started'))
const notStartedText = computed(() => t('reports.tasks.notStarted'))
const tasksText = computed(() => t('reports.tasks.tasks'))
const lastChangedText = computed(() => `${t('reports.tasks.lastChanged')}: ${formatReportTimestamp(props.userTask.lastActivity)}`)
</script>

<template>
  <component
    :is="completionStatus !== 'notStarted' ? 'button' : 'div'"
    :type="completionStatus !== 'notStarted' ? 'button' : undefined"
    class="group border-green-40 flex w-full items-stretch rounded-lg border bg-white text-left transition-all duration-200"
    :class="{
      'hover:bg-gray-2 hover:border-green-30 hover:shadow-[0_0_0_1px_theme(colors.green.30)] cursor-pointer focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-offset-2': completionStatus !== 'notStarted',
      'cursor-default': completionStatus === 'notStarted',
    }"
    :data-pendo="completionStatus !== 'notStarted' ? PendoFeature.ActivityReports.TaskRow : undefined"
    @click="completionStatus !== 'notStarted' ? emit('click') : undefined"
  >
    <div class="grid flex-1 grid-cols-1 gap-2 p-3 sm:grid-cols-3 sm:gap-0 sm:p-5">
      <div class="border-green-10 sm:border-green-10 flex min-w-0 flex-col justify-between border-b pb-2 sm:min-h-[80px] sm:border-r sm:border-b-0 sm:pr-4 sm:pb-0">
        <div class="text-green-40 min-h-5 text-base font-medium">
          <p class="break-words" v-text="displayName" />
        </div>
        <div v-if="userTask.lastActivity" class="text-green-40 text-sm">
          <p class="break-words" v-text="lastChangedText" />
        </div>
      </div>

      <div class="border-green-10 flex flex-col justify-start sm:min-h-[80px] sm:border-b-0 sm:px-4 sm:pb-0" :class="{ 'border-b pb-2': completionStatus !== 'notStarted', 'sm:border-green-10 sm:border-r': completionStatus !== 'notStarted' }">
        <div class="text-green-40 flex items-center gap-2 text-base font-medium">
          <span v-if="completionStatus === 'completed'" v-text="completedText" />
          <span v-else-if="completionStatus === 'started'" v-text="startedText" />
          <span v-else v-text="notStartedText" />
          <FontAwesomeIcon
            v-if="completionStatus === 'completed'"
            :icon="faCheck"
            class="text-green-40"
            aria-hidden="true"
          />
          <span v-if="completionStatus === 'completed'" class="sr-only" v-text="completedText" />
        </div>
      </div>

      <div v-if="completionStatus !== 'notStarted'" class="flex flex-col justify-start sm:min-h-[80px] sm:pl-4">
        <div class="text-green-40 mb-1 text-base font-medium sm:mb-2" v-text="tasksText" />
        <div class="flex flex-wrap gap-1">
          <template v-for="taskIndex in (totalTaskCount || Math.max(renderItems.length, 1))" :key="taskIndex">
            <div class="flex flex-col items-center">
              <FontAwesomeIcon
                :icon="getTaskCircleIcon(renderItems[taskIndex - 1] || null).icon"
                :class="getTaskCircleIcon(renderItems[taskIndex - 1] || null).class"
                :title="getTaskCircleIcon(renderItems[taskIndex - 1] || null).title"
                class="mb-0.5 h-6 w-6 text-xl md:h-8 md:w-8 md:text-2xl"
                aria-hidden="true"
              />
              <div class="text-green-40 text-xs md:text-sm" v-text="taskIndex" />
              <span class="sr-only" v-text="getTaskCircleIcon(renderItems[taskIndex - 1] || null).title" />
            </div>
          </template>
        </div>
      </div>
      <div v-else class="sm:min-h-[80px]">
        <!-- Empty column for alignment when no tasks -->
      </div>
    </div>

    <div
      class="flex w-8 items-center justify-center self-stretch rounded-r transition-colors duration-200"
      :class="[
        completionStatus !== 'notStarted' ? 'bg-green-40 group-hover:bg-green-30' : '',
        completionStatus === 'notStarted' ? 'hidden sm:flex' : '',
      ]"
    >
      <svg
        v-if="completionStatus !== 'notStarted'"
        class="h-12 w-12 text-white"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </div>
  </component>
</template>
